import { JsonPipe, NgOptimizedImage } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { Observable, zip } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { MasterDataEnum } from '@shared/enum/master-data.enum';
import { TDStorePage } from '@shared/enum/td-store-page.enum';
import { GraphqlQueryObject } from '@shared/gql/common.gql';
import { MerchantRequestViewResponse, WeekEnum } from '@shared/models';
import { WarehouseListContent } from '@shared/models/warehouse.model';
import { MasterService } from '@shared/services/master.service';
import { WarehouseListRequestAction } from '@shared/store/actions/warehouse.actions';
import { selectAllWarehouse } from '@shared/store/selectors/warehouse.selectors';
import { AppStates } from '@shared/store/state/app.states';
import { getSelectByPage } from '@shared/utils/get-select-by-page-util';

import { environment } from '../../../../../../environments/environment';

@Component({
  selector: 'app-table-order-schedule',
  standalone: true,
  imports: [JsonPipe, NgOptimizedImage],
  templateUrl: './table-order-schedule.component.html',
  styleUrl: './table-order-schedule.component.scss'
})
export class TableOrderScheduleComponent implements OnInit {
  protected readonly env = environment;
  @Input() page: TDStorePage;

  configTemplate: Observable<any[]>;
  cjLoadingGroupMapping$: Observable<any[]>;
  _defaultConfigs = [];
  public merchantRequestView$: Observable<MerchantRequestViewResponse>;
  public warehouseList$: Observable<WarehouseListContent[]>;
  private localStore: Observable<any>;
  orderScheduleList = signal<any[]>([]);

  constructor(
    private readonly store: Store<AppStates>,
    private ref: DestroyRef,
    private masterService: MasterService
  ) {}

  ngOnInit(): void {
    this.initState();
    this.getOrderScheduleList();
  }

  private getOrderScheduleList() {
    zip([this.merchantRequestView$, this.warehouseList$, this.configTemplate, this.cjLoadingGroupMapping$])
      .pipe(
        filter(
          ([value, warehouses, configs, cjLoadingGroupMapping]) =>
            !!value &&
            !!value?.orderSchedule?.orderScheduleList?.[0]?.schedules?.length &&
            !!warehouses &&
            warehouses?.length > 0 &&
            !!configs &&
            !!cjLoadingGroupMapping
        )
      )
      .subscribe(([value, warehouses, configs, cjLoadingGroupMapping]) => {
        this.orderScheduleList.set(this.mappingOrderScheduleList(configs, cjLoadingGroupMapping, value, warehouses));
      });
  }

  mappingOrderScheduleList(configs, cjLoadingGroupMapping, value, warehouses) {
    const orderSchedules = configs
      .reduce(
        (acc, config) => {
          const configType = config.storageTypes;
          const cjLoadingGroup = cjLoadingGroupMapping.find(v => v.storageType === configType);
          const type = `${cjLoadingGroup.code} ${cjLoadingGroup.name}`;
          if (config.isDefaultStorageType) {
            const schedules = value?.orderSchedule?.orderScheduleList ?? [];
            const mapped = schedules.map(schedule => {
              const warehouse = warehouses.find(wh => wh.code === schedule.warehouseCode);
              return {
                warehouseName: warehouse?.name,
                warehouseCode: schedule.warehouseCode,
                wmsCode: warehouse?.wmsCode,
                schedule: schedule.schedules,
                type: type
              };
            });

            return [...acc, ...mapped];
          }

          const storageTypes = value?.storageTypes || [];
          if (!storageTypes?.length) return acc;

          const storageTypeValue = storageTypes.find(v => v.type?.toUpperCase() === configType?.toUpperCase());
          if (!storageTypeValue) return acc;
          const warehouse = warehouses.find(wh => wh.code === storageTypeValue.warehouseCode);
          const oneItem = {
            warehouseName: warehouse?.name,
            warehouseCode: storageTypeValue.warehouseCode,
            wmsCode: warehouse?.wmsCode,
            schedule: storageTypeValue.schedules,
            type: type
          };

          return [...acc, oneItem];
        },
        [] as Array<{
          warehouseName?: string;
          warehouseCode?: string;
          schedule?: any;
          type?: any;
        }>
      )
      .reduce((acc, curr) => {
        if (!acc.has(curr.warehouseCode)) {
          acc.set(
            curr.warehouseCode,
            new Map([
              ['warehouseName', curr.warehouseName],
              ['wmsCode', curr.wmsCode]
            ])
          );
        }
        const key = curr.schedule
          .map(schedule => `${schedule.orderScheduleDate}-${schedule.deliverySchedule}-${schedule.pickScheduleDate}`)
          .join(',');
        if (!acc.get(curr.warehouseCode).has(key)) {
          acc.get(curr.warehouseCode).set(
            key,
            new Map([
              ['schedule', curr.schedule],
              ['type', []]
            ])
          );
        }
        acc.get(curr.warehouseCode).get(key).get('type').push(curr.type);
        return acc;
      }, new Map());

    return Array.from(orderSchedules.values())
      .map((scheduleMap: any) => {
        const warehouseName = scheduleMap.get('warehouseName');
        const wmsCode = scheduleMap.get('wmsCode');
        let warehouseRowSpan = 1;
        // skip key (warehouseName,wmsCode)
        const scheduleItems = Array.from(scheduleMap.values()).slice(2);

        const scheduleList = scheduleItems
          .map((value: any) => {
            const sorted = [...value.get('schedule')].sort(
              (a, b) => WeekEnum[a.orderScheduleDate as string] - WeekEnum[b.orderScheduleDate as string]
            );
            warehouseRowSpan += sorted.length + 1;
            return {
              scheduleDay: sorted,
              type: value.get('type'),
              rowSpan: sorted.length + 1
            };
          })
          .sort((a, b) => a.type[0].localeCompare(b.type[0]));

        return {
          warehouseName: `${wmsCode}-${warehouseName}`,
          scheduleList,
          rowSpan: warehouseRowSpan
        };
      })
      .sort((a, b) => a.warehouseName.localeCompare(b.warehouseName));
  }

  private initState() {
    this.cjLoadingGroupMapping();
    this.store.dispatch(new WarehouseListRequestAction('-'));
    this.localStore = this.localStore = this.store.pipe(takeUntilDestroyed(this.ref));
    this.merchantRequestView$ = this.localStore.pipe(
      select(getSelectByPage(this.page)),
      filter(value => value !== null)
    );
    this.warehouseList$ = this.localStore.pipe(
      select(selectAllWarehouse('Warehouse')),
      filter(warehouses => !!warehouses && warehouses.length > 0),
      take(1)
    );
  }

  cjLoadingGroupMapping() {
    const storageTypeQuery = new GraphqlQueryObject();
    storageTypeQuery.name = MasterDataEnum.CJ_LOADING_GROUP_MAPPINGS;
    storageTypeQuery.fields = ['code', 'name', 'storageType'];
    this.cjLoadingGroupMapping$ = this.masterService.getMasterDataByNames([storageTypeQuery]).pipe(
      filter(Boolean),
      map(result => result.data[MasterDataEnum.CJ_LOADING_GROUP_MAPPINGS])
    );

    this.configTemplate = this.cjLoadingGroupMapping$.pipe(
      map(cjLoadingGroupMapping => this.getConfigTemplate(cjLoadingGroupMapping))
    );
  }

  getConfigTemplate(value: { storageType: string }[]) {
    return value.map((config, index: number) => {
      return {
        id: index === 0 ? 'ORDER_POLICY_TYPE' : `${config?.storageType?.toUpperCase()}_POLICY_TYPE`,
        storageTypes: config.storageType,
        isDefaultStorageType: index === 0
      };
    });
  }
}
